// @ts-nocheck
import {
  clamp,
  len,
} from "../../util";
import {
  SPRITE_BRIM,
  bossImageByOct,
  drawEntityGroundShadow,
  octIndex,
  warlockImageByOct,
} from "../../sprites";
import { Sound } from "../../audio";
import { Game } from "../game-base";

Game.SPRITE_TRAIL_MAX = 14;
Game.BT_SPRITE_TRAIL_MAX = 48;
Game.SPRITE_TRAIL_NEW_ALPHA = 0.78;
Game.TIMESHIFT_GHOST_ALPHA = 0.5;
Game.SPRITE_TRAIL_PRIOR_DECAY = 0.76;
Game.SPRITE_TRAIL_TIME_FADE = 0.55;
Game.BT_TRAIL_MIN_MOVE_MUL = 0.62;
Game.BT_TRAIL_FLOOR = 0.07;       // asymptote — ghosts never fully vanish during bullet time
Game.BT_TRAIL_RIPPLE_RATE = 0.88; // each new ghost ripples prior ones toward the floor

Game.prototype.btTrailDiminish = function (alpha, rate) {
  const floor = Game.BT_TRAIL_FLOOR;
  return floor + (alpha - floor) * rate;
};

Game.prototype.bulletTimeOwnerPlayer = function () {
  const id = this._bulletTimeOwnerId;
  if (id == null) return null;
  if (this.playerById) {
    const p = this.playerById[id] ?? this.playerById[String(id)];
    if (p) return p;
  }
  return this.players.find(pl => pl.id === id) || null;
};

Game.prototype.trailSampleKey = function (mode, playerId) {
  return mode + ":" + playerId;
};

Game.prototype.trailPlayerPos = function (p) {
  if (p.isHuman && this.pred && this.pred.init) {
    return { x: this.pred.x, y: this.pred.y };
  }
  return { x: p.x, y: p.y };
};

Game.prototype.makeSpriteTrailGhost = function (p, x, y, alpha, mode) {
  const cx = Math.cos(p.facing);
  let faceDir = p.faceDir;
  if (faceDir == null) faceDir = Math.abs(cx) > 0.2 ? (cx < 0 ? -1 : 1) : 1;
  return {
    mode,
    playerId: p.id,
    x,
    y,
    facing: p.facing,
    spriteOct: p.spriteOct != null ? p.spriteOct : octIndex(p.facing),
    index: p.index,
    radius: p.radius,
    boss: p.boss,
    faceDir,
    robe: p.robe,
    trim: p.trim,
    alpha,
  };
};

Game.prototype.trySpawnSpriteTrail = function (p, mode, opts) {
  if (!this.spriteTrails) this.spriteTrails = [];
  if (!this._trailLastPos) this._trailLastPos = {};
  const key = this.trailSampleKey(mode, p.id);
  const { x: px, y: py } = this.trailPlayerPos(p);
  const minMove = p.radius * opts.minMoveMul;
  const last = this._trailLastPos[key];
  if (!last) {
    this._trailLastPos[key] = { x: px, y: py };
    return;
  }
  if (len(px - last.x, py - last.y) < minMove) return;

  if (opts.decayPrior) {
    for (const t of this.spriteTrails) {
      if (t.mode === mode && t.playerId === p.id) t.alpha *= opts.priorDecay;
    }
  }

  this.spriteTrails.push(this.makeSpriteTrailGhost(p, last.x, last.y, opts.newAlpha, mode));

  let n = 0;
  for (const t of this.spriteTrails) {
    if (t.mode === mode && t.playerId === p.id) n++;
  }
  while (n > opts.maxCount) {
    const idx = this.spriteTrails.findIndex(t => t.mode === mode && t.playerId === p.id);
    if (idx < 0) break;
    this.spriteTrails.splice(idx, 1);
    n--;
  }

  this._trailLastPos[key] = { x: px, y: py };
};

Game.prototype.trailPlayerMatch = function (trailPlayerId, ownerId) {
  return trailPlayerId == ownerId;
};

Game.prototype.isBulletTimeTrail = function (t) {
  return t.mode === "bulletTime" || t.mode === "bulletTimeCatchUp";
};

Game.prototype.bulletTimeTrailsFor = function (playerId) {
  if (!this.spriteTrails) return [];
  return this.spriteTrails.filter(t => this.isBulletTimeTrail(t) && this.trailPlayerMatch(t.playerId, playerId));
};

Game.prototype.trailOwnerPlayer = function (ownerId) {
  if (ownerId == null) return null;
  if (this.playerById) {
    const p = this.playerById[ownerId] ?? this.playerById[String(ownerId)];
    if (p) return p;
  }
  return this.players.find(pl => pl.id == ownerId) || null;
};

Game.prototype.btTrailChainLength = function (ghosts, tx, ty) {
  if (!ghosts.length) return 0;
  let total = 0;
  for (let i = 0; i < ghosts.length - 1; i++) {
    total += len(ghosts[i].g.x - ghosts[i + 1].g.x, ghosts[i].g.y - ghosts[i + 1].g.y);
  }
  const tail = ghosts[ghosts.length - 1].g;
  total += len(tx - tail.x, ty - tail.y);
  return total;
};

Game.prototype.beginBulletTimeTrailCatchUp = function (ownerId) {
  if (ownerId == null || this._btCatchUp) return;
  const trails = this.spriteTrails.filter(t => t.mode === "bulletTime" && this.trailPlayerMatch(t.playerId, ownerId));
  if (!trails.length) return;

  const p = this.trailOwnerPlayer(ownerId);
  let tx;
  let ty;
  if (p) {
    const pos = this.trailPlayerPos(p);
    tx = pos.x;
    ty = pos.y;
  } else {
    tx = trails[trails.length - 1].x;
    ty = trails[trails.length - 1].y;
  }

  const dur = Sound.bulletTimeEndDuration();
  const ghosts = trails.map(g => {
    g.mode = "bulletTimeCatchUp";
    return { g, ox: g.x, oy: g.y, startAlpha: g.alpha };
  });
  const chainLen = this.btTrailChainLength(ghosts, tx, ty);

  this._btCatchUp = {
    ownerId,
    dur,
    t: 0,
    tx,
    ty,
    speed: Math.max(chainLen / Math.max(dur, 0.001), 420),
    ghosts,
  };
};

Game.prototype.endBulletTimeTrailCatchUp = function () {
  const id = this._btCatchUp?.ownerId;
  this._btCatchUp = null;
  if (id != null) this.clearSpriteTrails("bulletTime", id);
};

Game.prototype.updateBulletTimeTrailCatchUp = function (dt) {
  const cu = this._btCatchUp;
  if (!cu) return;

  cu.t += dt;
  const p = this.trailOwnerPlayer(cu.ownerId);
  if (p) {
    const pos = this.trailPlayerPos(p);
    cu.tx = pos.x;
    cu.ty = pos.y;
  }

  const mergeR = (cu.ghosts[0]?.g.radius || 18) * 0.32;
  const step = cu.speed * dt;

  for (let i = cu.ghosts.length - 1; i >= 0; i--) {
    const entry = cu.ghosts[i];
    const g = entry.g;
    let tx;
    let ty;
    if (i === cu.ghosts.length - 1) {
      tx = cu.tx;
      ty = cu.ty;
    } else {
      tx = cu.ghosts[i + 1].g.x;
      ty = cu.ghosts[i + 1].g.y;
    }

    const dx = tx - g.x;
    const dy = ty - g.y;
    const dist = len(dx, dy);
    if (dist <= mergeR) {
      g.x = tx;
      g.y = ty;
      g.alpha = 0;
      continue;
    }

    const move = Math.min(dist, step);
    g.x += (dx / dist) * move;
    g.y += (dy / dist) * move;
    g.facing = Math.atan2(dy, dx);
    const cx = Math.cos(g.facing);
    g.faceDir = Math.abs(cx) > 0.2 ? (cx < 0 ? -1 : 1) : (g.faceDir || 1);
    g.spriteOct = octIndex(g.facing);

    const traveled = len(g.x - entry.ox, g.y - entry.oy);
    const span = len(entry.ox - tx, entry.oy - ty);
    const prog = span > 1 ? clamp(traveled / span, 0, 1) : 1;
    g.alpha = entry.startAlpha * (1 - prog * 0.92);
  }

  while (cu.ghosts.length) {
    const entry = cu.ghosts[0];
    const g = entry.g;
    let tx;
    let ty;
    if (cu.ghosts.length === 1) {
      tx = cu.tx;
      ty = cu.ty;
    } else {
      tx = cu.ghosts[1].g.x;
      ty = cu.ghosts[1].g.y;
    }
    if (len(tx - g.x, ty - g.y) > mergeR) break;
    const idx = this.spriteTrails.indexOf(g);
    if (idx >= 0) this.spriteTrails.splice(idx, 1);
    cu.ghosts.shift();
  }

  if (cu.t >= cu.dur || !cu.ghosts.length) this.endBulletTimeTrailCatchUp();
};

// Bullet time ripple: newest ghost always at 100%; each spawn dims older copies
// (diminishing steps toward BT_TRAIL_FLOOR) so alpha rises toward the live sprite.
Game.prototype.trySpawnBulletTimeTrail = function (p) {
  if (!this.spriteTrails) this.spriteTrails = [];
  if (!this._trailLastPos) this._trailLastPos = {};
  const key = this.trailSampleKey("bulletTime", p.id);
  const { x: px, y: py } = this.trailPlayerPos(p);
  const minMove = p.radius * Game.BT_TRAIL_MIN_MOVE_MUL;
  const last = this._trailLastPos[key];
  if (!last) {
    this._trailLastPos[key] = { x: px, y: py };
    return;
  }
  if (len(px - last.x, py - last.y) < minMove) return;

  for (const t of this.bulletTimeTrailsFor(p.id)) {
    t.alpha = this.btTrailDiminish(t.alpha, Game.BT_TRAIL_RIPPLE_RATE);
  }

  this.spriteTrails.push(this.makeSpriteTrailGhost(p, last.x, last.y, 1, "bulletTime"));

  let count = this.bulletTimeTrailsFor(p.id).length;
  while (count > Game.BT_SPRITE_TRAIL_MAX) {
    const idx = this.spriteTrails.findIndex(t => t.mode === "bulletTime" && t.playerId === p.id);
    if (idx < 0) break;
    this.spriteTrails.splice(idx, 1);
    count--;
  }

  this._trailLastPos[key] = { x: px, y: py };
};

Game.prototype.syncTimeShiftGhost = function (p) {
  const g = p._tsGhost;
  if (!g) {
    this.clearSpriteTrails("timeShift", p.id);
    return;
  }
  if (!this.spriteTrails) this.spriteTrails = [];
  let ghost = this.spriteTrails.find(t => t.mode === "timeShift" && this.trailPlayerMatch(t.playerId, p.id));
  if (!ghost) {
    ghost = this.makeSpriteTrailGhost(p, g.x, g.y, Game.TIMESHIFT_GHOST_ALPHA, "timeShift");
    this.spriteTrails.push(ghost);
  }
  ghost.x = g.x;
  ghost.y = g.y;
  ghost.facing = g.a;
  ghost.alpha = Game.TIMESHIFT_GHOST_ALPHA;
  const cx = Math.cos(g.a);
  ghost.faceDir = Math.abs(cx) > 0.2 ? (cx < 0 ? -1 : 1) : (ghost.faceDir || 1);
  ghost.spriteOct = octIndex(g.a);
};

Game.prototype.clearSpriteTrails = function (mode, playerId) {
  if (mode === "bulletTime" && this._btCatchUp) {
    if (playerId == null || this._btCatchUp.ownerId == playerId) this._btCatchUp = null;
  }
  if (!this.spriteTrails) return;
  const drop = (t) => {
    if (mode === "bulletTime") return this.isBulletTimeTrail(t);
    return t.mode === mode;
  };
  if (playerId == null) {
    this.spriteTrails = this.spriteTrails.filter(t => !drop(t));
  } else {
    this.spriteTrails = this.spriteTrails.filter(t => !drop(t) || !this.trailPlayerMatch(t.playerId, playerId));
  }
  if (!this._trailLastPos) return;
  if (playerId == null) {
    for (const k of Object.keys(this._trailLastPos)) {
      if (k.startsWith(mode + ":")) delete this._trailLastPos[k];
    }
  } else {
    delete this._trailLastPos[this.trailSampleKey(mode, playerId)];
  }
};

Game.prototype.updateSpriteTrails = function (dt) {
  if (!this.spriteTrails) this.spriteTrails = [];

  const fadeStep = dt * Game.SPRITE_TRAIL_TIME_FADE;
  for (const t of this.spriteTrails) {
    if (t.mode === "rush") t.alpha -= fadeStep;
  }
  this.spriteTrails = this.spriteTrails.filter(t =>
    this.isBulletTimeTrail(t) || t.mode === "timeShift" || t.alpha > 0.025);

  if (this._btCatchUp) {
    this.updateBulletTimeTrailCatchUp(dt);
  } else if (!this._bulletTimeActive) {
    for (const t of this.spriteTrails) {
      if (t.mode !== "bulletTime") continue;
      this.beginBulletTimeTrailCatchUp(t.playerId);
      break;
    }
  }

  if (this._bulletTimeActive && this._bulletTimeOwnerId != null && !this._btCatchUp) {
    const p = this.bulletTimeOwnerPlayer();
    if (p && p.alive && !p.falling) {
      this.trySpawnBulletTimeTrail(p);
    } else {
      this.clearSpriteTrails("bulletTime", this._bulletTimeOwnerId);
    }
  }

  for (const p of this.players) {
    if (p._tsGhost) this.syncTimeShiftGhost(p);
    else this.clearSpriteTrails("timeShift", p.id);

    if (!p.alive || p.falling || !(p.rushT > 0)) {
      if (!(p.rushT > 0) && this._trailLastPos) {
        delete this._trailLastPos[this.trailSampleKey("rush", p.id)];
      }
      continue;
    }
    this.trySpawnSpriteTrail(p, "rush", {
      minMoveMul: 0.22,
      newAlpha: Game.SPRITE_TRAIL_NEW_ALPHA,
      priorDecay: Game.SPRITE_TRAIL_PRIOR_DECAY,
      maxCount: Game.SPRITE_TRAIL_MAX,
      decayPrior: true,
    });
  }
};

Game.prototype.drawSpriteTrailGhost = function (g) {
  const c = this.ctx;
  if (!g || g.alpha <= 0.025) return;

  const ghost = {
    x: 0, y: 0, radius: g.radius, boss: g.boss, facing: g.facing,
    robe: g.robe, trim: g.trim, index: g.index,
  };

  c.save();
  c.translate(g.x, g.y);
  const alphaFloor = g.mode === "timeShift" ? Game.TIMESHIFT_GHOST_ALPHA
    : g.mode === "bulletTimeCatchUp" ? 0
    : (g.mode === "bulletTime" ? Game.BT_TRAIL_FLOOR : 0);
  c.globalAlpha = clamp(g.alpha, alphaFloor, 1);

  drawEntityGroundShadow(c, ghost);

  this.upright();

  const oct = g.spriteOct != null ? g.spriteOct : octIndex(g.facing);
  const img = (g.boss ? bossImageByOct(g.boss, oct) : null) || warlockImageByOct(g.index, oct);
  if (img) {
    const baseH = g.boss ? g.radius * 10.5 : g.radius * 7.6;
    const aspect = img.naturalWidth / img.naturalHeight;
    const dw = baseH * aspect;
    c.drawImage(img, -dw / 2, -baseH / 2 - g.radius * 0.5, dw, baseH);
  } else {
    const spr = this.sprite(g);
    const ss = (g.radius / SPRITE_BRIM) * 1.15;
    c.scale(ss * (g.faceDir || 1), ss);
    c.drawImage(spr, -spr.width / 2, -spr.height / 2);
  }

  c.restore();
};
